import { useState, useEffect } from "react";
import { Modal, TextInput, Button, Group, Alert } from "@mantine/core";
import { api, type Family } from "../services/api";

interface EditFamilyModalProps {
  opened: boolean;
  onClose: () => void;
  family: Family | null;
  onSuccess: () => void;
}

export function EditFamilyModal({ opened, onClose, family, onSuccess }: EditFamilyModalProps) {
  const [familyName, setFamilyName] = useState("");
  const [uniqueLink, setUniqueLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (family) {
      setFamilyName(family.family_name);
      setUniqueLink(family.unique_link);
    }
  }, [family]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!family || !familyName.trim() || !uniqueLink.trim()) {
      setError("All fields are required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.updateFamily(family.id, familyName.trim(), uniqueLink.trim());
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update family. Link might already exist.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal 
      opened={opened} 
      onClose={handleClose} 
      title="Edit Family" 
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert color="red" style={{ marginBottom: "1rem" }}>
            {error}
          </Alert>
        )}

        <TextInput
          label="Family Name"
          placeholder="e.g., Smith Family"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          required
          style={{ marginBottom: "1rem" }}
        />

        <TextInput
          label="Unique Link"
          placeholder="e.g., smith-family-2026"
          value={uniqueLink}
          onChange={(e) => setUniqueLink(e.target.value)}
          required
          description="This will be used in the invitation URL"
          style={{ marginBottom: "1.5rem" }}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Family"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
